"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Area, AreaChart, XAxis, YAxis } from "recharts";
import { ArrowRight, ArrowUpRight, ChartBar as BarChart3, Building2, CircleCheck as CheckCircle2, Compass, Globe as Globe2, Layers, ChartLine as LineChart, MapPin, Search, ShieldCheck, Sparkles, Target, TrendingUp } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const quarterlyTrends = [{
  quarter: "Q1 2024",
  searchVolume: 42000,
  aiOverviews: 12000
}, {
  quarter: "Q2 2024",
  searchVolume: 48500,
  aiOverviews: 16500
}, {
  quarter: "Q3 2024",
  searchVolume: 56000,
  aiOverviews: 22000
}, {
  quarter: "Q4 2024",
  searchVolume: 64200,
  aiOverviews: 29800
}, {
  quarter: "Q1 2025",
  searchVolume: 73000,
  aiOverviews: 38500
}];
const localKeywords = [{
  keyword: "SEO consultant Northampton",
  volume: "1,200/mo",
  difficulty: "Medium (38%)",
  intent: "Commercial",
  opportunity: "High"
}, {
  keyword: "B2B logistics services Northampton",
  volume: "2,400/mo",
  difficulty: "High (52%)",
  intent: "Transactional",
  opportunity: "Very High"
}, {
  keyword: "Commercial lawyer Northamptonshire",
  volume: "880/mo",
  difficulty: "Low (26%)",
  intent: "Commercial",
  opportunity: "High"
}, {
  keyword: "Industrial equipment suppliers Northampton",
  volume: "1,850/mo",
  difficulty: "Medium (34%)",
  intent: "Transactional",
  opportunity: "High"
}, {
  keyword: "Ecommerce fulfilment Northampton",
  volume: "1,100/mo",
  difficulty: "Medium (41%)",
  intent: "Transactional",
  opportunity: "Medium"
}];
const strategicOpportunities = [{
  title: "Northampton Local Pack Dominance",
  description: "Over 64% of high-intent B2B and consumer searches in Northampton trigger Google Local Map Packs. Optimising local entity signals captures ready-to-buy traffic.",
  icon: MapPin,
  stat: "64%",
  label: "Local Search Intent"
}, {
  title: "AI Overview Visibility Gap",
  description: "Fewer than 18% of Northamptonshire businesses are structured for Google AI Overviews. I build structured schema and authority footprints to secure conversational AI placements.",
  icon: Sparkles,
  stat: "82%",
  label: "Competitor AI Gap"
}, {
  title: "Regional B2B Lead Conversion",
  description: "Northampton businesses sitting on the M1 corridor often miss high-value search queries originating from nearby industrial hubs like Milton Keynes and Rugby.",
  icon: Target,
  stat: "3.4x",
  label: "Search Lead Multiplier"
}];
const neighboringRegions = [{
  name: "Milton Keynes",
  path: "/LocalSEOPage",
  distance: "18 miles",
  focus: "Tech & Corporate Logistics Corridor"
}, {
  name: "Bedford",
  path: "/LocalSEOPage",
  distance: "21 miles",
  focus: "B2B & Professional Services"
}, {
  name: "Rugby",
  path: "/NationalSeoPage",
  distance: "20 miles",
  focus: "Industrial Warehousing & Distribution"
}, {
  name: "Leicester",
  path: "/LocalSEOPage",
  distance: "38 miles",
  focus: "E-Commerce & Manufacturing Hub"
}, {
  name: "Coventry",
  path: "/LocalSEOPage",
  distance: "32 miles",
  focus: "Engineering & Advanced Mobility"
}, {
  name: "Cambridge",
  path: "/InternationalSeoPage",
  distance: "50 miles",
  focus: "Life Sciences & Tech Innovation"
}];
const chartConfig = {
  searchVolume: {
    label: "Organic Search Volume",
    color: "hsl(var(--primary))"
  },
  aiOverviews: {
    label: "AI Overview Queries",
    color: "hsl(var(--accent))"
  }
};
export default function Northamptonmarketanalysis() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".gsap-reveal", {
        y: 28
      });
      gsap.to(".gsap-reveal", {
        opacity: 1,
        y: 0,
        duration: 0.65,
        stagger: 0.08,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".gsap-reveal", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  return <section id="northamptonmarketanalysis" data-nav="light" ref={containerRef} className="relative bg-background text-foreground py-16 md:py-24 lg:py-28 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16 gsap-reveal" style={{
        opacity: 0
      }}>
          <div className="inline-flex items-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider">
              <Compass className="size-3.5 text-primary" />
              {"Regional Market Analysis"}
            </Badge>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif tracking-tight text-foreground mb-6">
            {"Northampton SEO & Search Market Analysis"}
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            {"As an independent SEO consultant, I conduct deep, tailored market research across Northamptonshire. You work directly with me to identify high-intent local search trends, uncover untapped keywords, and claim market share along the M1 commercial corridor."}
          </p>
        </div>

        {/* Key Market Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12 lg:mb-16">
          <Card className="gsap-reveal bg-card border-border shadow-xs hover:shadow-md transition-shadow" style={{
          opacity: 0
        }}>
            <CardContent className="p-5 md:p-6 text-center">
              <div className="inline-flex items-center justify-center p-2.5 rounded-lg bg-primary/10 text-primary mb-3">
                <TrendingUp className="size-5" />
              </div>
              <div className="text-2xl md:text-3xl font-bold text-foreground font-serif">+38%</div>
              <p className="text-xs md:text-sm text-muted-foreground mt-1 font-medium">
                {"Northampton B2B Search Growth"}
              </p>
            </CardContent>
          </Card>

          <Card className="gsap-reveal bg-card border-border shadow-xs hover:shadow-md transition-shadow" style={{
          opacity: 0
        }}>
            <CardContent className="p-5 md:p-6 text-center">
              <div className="inline-flex items-center justify-center p-2.5 rounded-lg bg-accent/10 text-accent mb-3">
                <Search className="size-5" />
              </div>
              <div className="text-2xl md:text-3xl font-bold text-foreground font-serif">145k+</div>
              <p className="text-xs md:text-sm text-muted-foreground mt-1 font-medium">
                {"Monthly Local Search Volume"}
              </p>
            </CardContent>
          </Card>

          <Card className="gsap-reveal bg-card border-border shadow-xs hover:shadow-md transition-shadow" style={{
          opacity: 0
        }}>
            <CardContent className="p-5 md:p-6 text-center">
              <div className="inline-flex items-center justify-center p-2.5 rounded-lg bg-primary/10 text-primary mb-3">
                <ShieldCheck className="size-5" />
              </div>
              <div className="text-2xl md:text-3xl font-bold text-foreground font-serif">28%</div>
              <p className="text-xs md:text-sm text-muted-foreground mt-1 font-medium">
                {"Avg. Organic Keyword Difficulty"}
              </p>
            </CardContent>
          </Card>

          <Card className="gsap-reveal bg-card border-border shadow-xs hover:shadow-md transition-shadow" style={{
          opacity: 0
        }}>
            <CardContent className="p-5 md:p-6 text-center">
              <div className="inline-flex items-center justify-center p-2.5 rounded-lg bg-accent/10 text-accent mb-3">
                <LineChart className="size-5" />
              </div>
              <div className="text-2xl md:text-3xl font-bold text-foreground font-serif">4.2x</div>
              <p className="text-xs md:text-sm text-muted-foreground mt-1 font-medium">
                {"Local ROI Potential"}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Growth & Search Volume Trend Visualisation */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center mb-16 lg:mb-20">
          <Card className="lg:col-span-7 bg-card border-border p-6 md:p-8 gsap-reveal shadow-sm" style={{
          opacity: 0
        }}>
            <CardHeader className="p-0 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-xl font-bold text-foreground font-serif">
                    {"Northamptonshire Search Trend Trajectory"}
                  </CardTitle>
                  <CardDescription className="text-sm text-muted-foreground mt-1">
                    {"Quarterly progression of organic search demand & AI Overview adoption"}
                  </CardDescription>
                </div>
                <Badge variant="outline" className="border-border text-foreground">
                  {"2024 - 2025 Data"}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ChartContainer config={chartConfig} className="h-64 sm:h-72 w-full">
                <AreaChart data={quarterlyTrends} margin={{
                top: 10,
                right: 10,
                left: -20,
                bottom: 0
              }}>
                  <XAxis dataKey="quarter" stroke="currentColor" className="text-xs text-muted-foreground" tickLine={false} axisLine={false} />
                  <YAxis stroke="currentColor" className="text-xs text-muted-foreground" tickLine={false} axisLine={false} tickFormatter={value => `${value / 1000}k`} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Area type="monotone" dataKey="searchVolume" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.15} strokeWidth={2.5} />
                  <Area type="monotone" dataKey="aiOverviews" stroke="hsl(var(--accent))" fill="hsl(var(--accent))" fillOpacity={0.2} strokeWidth={2} />
                </AreaChart>
              </ChartContainer>
            </CardContent>
          </Card>

          <div className="lg:col-span-5 space-y-6 gsap-reveal" style={{
          opacity: 0
        }}>
            <div className="space-y-2">
              <Badge variant="secondary" className="text-xs font-semibold">
                {"Consultant Analysis"}
              </Badge>
              <h3 className="text-2xl font-bold font-serif text-foreground">
                {"Why Northampton Businesses Are Positioned for Organic Growth"}
              </h3>
            </div>
            <p className="text-muted-foreground leading-relaxed text-sm sm:text-base">
              {"Northampton sits at a strategic crossroads for logistics, B2B services, and industrial manufacturing. However, many local corporate websites suffer from thin location pages and outdated technical architecture."}
            </p>
            <div className="space-y-3 pt-2">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                <p className="text-sm text-foreground">
                  <strong className="font-semibold text-foreground">{"Direct Strategy:"}</strong> {"You collaborate directly with me—no account managers or junior templates."}
                </p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                <p className="text-sm text-foreground">
                  <strong className="font-semibold text-foreground">{"Search Intent Alignment:"}</strong> {"We map high-margin commercial intent queries specific to Northamptonshire."}
                </p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                <p className="text-sm text-foreground">
                  <strong className="font-semibold text-foreground">{"AI Overview Readiness:"}</strong> {"Optimising entity content for Google Gemini and ChatGPT search discovery."}
                </p>
              </div>
            </div>
            <div className="pt-4">
              <Button asChild variant="default" size="lg" className="rounded-md font-medium">
                <Link to="/ContactPage">
                  {"Request Northampton SEO Consultation"}
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Local Target Keywords Table */}
        <div className="mb-16 lg:mb-20 gsap-reveal" style={{
        opacity: 0
      }}>
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
            <div>
              <Badge variant="secondary" className="mb-2 text-xs font-semibold">
                {"High-Value Opportunities"}
              </Badge>
              <h3 className="text-2xl md:text-3xl font-bold font-serif text-foreground">
                {"Representative Northampton Search Opportunities"}
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              {"A sample of verified commercial keyword clusters across key Northamptonshire business sectors."}
            </p>
          </div>

          <Card className="bg-card border-border shadow-xs overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-border bg-muted/50 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <th className="py-4 px-6">{"Target Keyword Cluster"}</th>
                    <th className="py-4 px-6">{"Est. Volume"}</th>
                    <th className="py-4 px-6">{"Keyword Difficulty"}</th>
                    <th className="py-4 px-6">{"Search Intent"}</th>
                    <th className="py-4 px-6 text-right">{"Opportunity Index"}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border text-sm">
                  {localKeywords.map((item, i) => <tr key={i} data-index={i} className="hover:bg-muted/30 transition-colors">
                      <td className="py-4 px-6 font-medium text-foreground flex items-center gap-2">
                        <Search className="size-4 text-primary shrink-0" />
                        <span>{item.keyword}</span>
                      </td>
                      <td className="py-4 px-6 text-muted-foreground">{item.volume}</td>
                      <td className="py-4 px-6">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-muted text-foreground border border-border">
                          {item.difficulty}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-muted-foreground">{item.intent}</td>
                      <td className="py-4 px-6 text-right">
                        <Badge variant={item.opportunity === "Very High" ? "default" : "secondary"} className="font-medium">
                          {item.opportunity}
                        </Badge>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* Strategic Opportunity Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16 lg:mb-20">
          {strategicOpportunities.map((item, i) => {
          const IconComponent = item.icon;
          return <Card key={i} data-index={i} className="gsap-reveal bg-card border-border shadow-xs hover:shadow-md transition-all flex flex-col justify-between" style={{
            opacity: 0
          }}>
                <CardHeader>
                  <div className="flex items-center justify-between mb-3">
                    <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                      <IconComponent className="size-6" />
                    </div>
                    <div className="text-right">
                      <span className="text-2xl font-bold font-serif text-primary block">{item.stat}</span>
                      <span className="text-[12.5px] text-muted-foreground uppercase tracking-wider font-semibold">
                        {item.label}
                      </span>
                    </div>
                  </div>
                  <CardTitle className="text-lg font-bold text-foreground">{item.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>;
        })}
        </div>

        {/* Regional Hubs & Inter-Regional Search */}
        <div className="bg-muted/40 rounded-2xl p-6 sm:p-8 lg:p-10 border border-border mb-16 lg:mb-20 gsap-reveal" style={{
        opacity: 0
      }}>
          <div className="max-w-2xl mb-8">
            <Badge variant="outline" className="mb-2 text-xs font-semibold border-border bg-background">
              <Globe2 className="size-3.5 text-primary" />
              {"Regional Search Network"}
            </Badge>
            <h3 className="text-2xl md:text-3xl font-bold font-serif text-foreground">
              {"Explore Nearby Search Markets & Local Hubs"}
            </h3>
            <p className="text-sm text-muted-foreground mt-2">
              {"Northamptonshire businesses often expand across neighboring commercial hubs. Discover how my tailored regional SEO strategies drive targeted traffic across East Midlands and Buckinghamshire."}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {neighboringRegions.map((region, i) => <Card key={i} data-index={i} className="bg-card border-border hover:border-primary/50 shadow-xs hover:shadow-md transition-all group">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <MapPin className="size-4 text-primary shrink-0" />
                        <h4 className="font-semibold text-foreground text-base group-hover:text-primary transition-colors">
                          {region.name}
                        </h4>
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">
                        {"Distance: "}
                        <span className="font-medium text-foreground">{region.distance}</span>
                      </p>
                      <p className="text-xs text-muted-foreground line-clamp-1">{region.focus}</p>
                    </div>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Link to={region.path} className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-primary transition-colors" aria-label={`Explore ${region.name} SEO strategies`}>
                            <ArrowUpRight className="size-4" />
                          </Link>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{"View " + region.name + " SEO Strategy"}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Separator className="my-3" />
                  <Link to={region.path} className="inline-flex items-center text-xs font-medium text-primary hover:underline gap-1">
                    {"Analyze " + region.name + " Market"}
                    <ArrowRight className="size-3" />
                  </Link>
                </CardContent>
              </Card>)}
          </div>
        </div>

        {/* Direct Action Banner */}
        <Card className="bg-primary text-primary-foreground border-none rounded-2xl shadow-xl overflow-hidden gsap-reveal" style={{
        opacity: 0
      }}>
          <CardContent className="p-8 sm:p-10 md:p-12 text-center md:text-left flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="max-w-2xl space-y-3">
              <Badge variant="secondary" className="bg-primary-foreground/10 text-primary-foreground border-none text-xs font-semibold">
                <Building2 className="size-3.5" />
                {"Work Directly With Yassine Essaadi"}
              </Badge>
              <h3 className="text-2xl sm:text-3xl font-bold font-serif leading-tight">
                {"Ready to Dominate Northampton Search Results?"}
              </h3>
              <p className="text-primary-foreground/80 text-sm sm:text-base leading-relaxed">
                {"Get a hands-on, expert SEO analysis tailored for your Northamptonshire business. No agency middlemen, no automated cookie-cutter reports—just a direct, transparent SEO roadmap."}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-4 shrink-0 w-full sm:w-auto">
              <Button asChild size="lg" variant="secondary" className="w-full sm:w-auto font-medium rounded-md">
                <Link to="/ContactPage">
                  {"Book Free Northampton Consultation"}
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="w-full sm:w-auto font-medium rounded-md border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                <Link to="/CaseStudiesPage">
                  {"View Case Studies"}
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>;
}